import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, MapPin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StorageLocations = () => {
  const { getAuthHeaders } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '', 
    building: '', 
    room: '' 
  });

  const fetchLocations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/storage-locations`, { headers: getAuthHeaders() });
      setLocations(response.data);
    } catch (error) {
      toast.error('Failed to fetch storage locations');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleOpenDialog = (location = null) => {
    if (location) {
      setSelectedLocation(location);
      setFormData({ 
        name: location.name, 
        description: location.description || '',
        building: location.building || '',
        room: location.room || ''
      });
    } else {
      setSelectedLocation(null);
      setFormData({ name: '', description: '', building: '', room: '' });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (selectedLocation) {
        await axios.put(`${API_URL}/api/storage-locations/${selectedLocation.id}`, formData, { headers: getAuthHeaders() });
        toast.success('Location updated successfully');
      } else {
        await axios.post(`${API_URL}/api/storage-locations`, formData, { headers: getAuthHeaders() });
        toast.success('Location created successfully');
      }
      setShowDialog(false);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save location');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/storage-locations/${selectedLocation.id}`, { headers: getAuthHeaders() });
      toast.success('Location deleted successfully');
      setShowDeleteDialog(false);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete location');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="locations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Storage Locations</h1>
          <p className="text-muted-foreground mt-1">Manage where equipment is stored</p>
        </div>
        <Button data-testid="add-location-btn" onClick={() => handleOpenDialog()}>
          <Plus className="w-4 h-4 mr-2" />
          Add Location
        </Button>
      </div>

      {/* Locations Table */}
      <Card className="border-border/50">
        <CardHeader className="flex flex-row items-center gap-2">
          <MapPin className="w-5 h-5 text-muted-foreground" />
          <CardTitle className="text-lg">All Locations ({locations.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Building</TableHead>
                <TableHead>Room</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {locations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                    No storage locations yet. Add your first location.
                  </TableCell>
                </TableRow>
              ) : (
                locations.map((location) => (
                  <TableRow key={location.id} className="table-row-hover" data-testid={`location-row-${location.id}`}>
                    <TableCell className="font-medium">{location.name}</TableCell>
                    <TableCell>{location.building || '-'}</TableCell>
                    <TableCell>{location.room || '-'}</TableCell>
                    <TableCell className="text-muted-foreground max-w-[200px] truncate">
                      {location.description || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`edit-location-${location.id}`}
                          onClick={() => handleOpenDialog(location)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`delete-location-${location.id}`}
                          onClick={() => { setSelectedLocation(location); setShowDeleteDialog(true); }}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{selectedLocation ? 'Edit Location' : 'Add New Location'}</DialogTitle>
            <DialogDescription>
              {selectedLocation ? 'Update the storage location details.' : 'Create a new storage location.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                data-testid="location-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Main Storage Room"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="building">Building</Label>
                <Input
                  id="building"
                  data-testid="location-building-input"
                  value={formData.building}
                  onChange={(e) => setFormData({ ...formData, building: e.target.value })}
                  placeholder="e.g., Building A"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="room">Room</Label>
                <Input
                  id="room"
                  data-testid="location-room-input"
                  value={formData.room}
                  onChange={(e) => setFormData({ ...formData, room: e.target.value })}
                  placeholder="e.g., Room 101"
                />
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                data-testid="location-description-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Details about this storage location..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button data-testid="save-location-btn" onClick={handleSave} disabled={!formData.name}>
              {selectedLocation ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Location</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedLocation?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" data-testid="confirm-delete-location" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StorageLocations;
